/* eslint-disable @typescript-eslint/no-explicit-any */
import { Required, IsString, IsNumber, IsStringOrNumber, DataValidator, transformKeys, IsArray } from "@/utils/type-vaildator";
import api from "./api";
import { ThinkingData } from "./push-info-origin-data-record-list";
import { AiMakeMessageRecordData } from "./ai-make-source-record-list-by-type";

export interface GetGenerateMessageResultRequest {
  taskId: string;
}

interface MakeGetTaskStatusResponse {
  id?: any;
  record_id?: any;
  agent_id?: any;
  type?: any;
  title?: any;
  content?: any;
  input_text?: any;
  cover_image_url?: any;
  video_url?: any;
  image_urls?: any;
  audio_url?: any;
  created_at?: any;
  task_id?: any;
  status?: any;
  video_duration?: any;
  audio_duration?: any;
}

export class MakeGetTaskStatusRawData {
  @Required("recordId 不能为空")
  @IsNumber("recordId 必须是数字")
  recordId: number;

  @IsString("agentId 必须是字符串")
  agentId?: string;

  @IsStringOrNumber("type 必须是字符串或数字")
  type?: string | number;

  @IsString("title 必须是字符串")
  title?: string;

  @IsString("content 必须是字符串")
  content?: string;

  @IsString("inputText 必须是字符串")
  inputText?: string;

  @IsString("coverImageUrl 必须是字符串")
  coverImageUrl?: string;

  @IsString("videoUrl 必须是字符串")
  videoUrl?: string;

  @IsString("imageUrls 必须是字符串")
  imageUrls?: string;

  @IsString("audioUrl 必须是字符串")
  audioUrl?: string;

  @IsStringOrNumber("createdAt 必须是字符串或数字")
  createdAt?: string | number;

  @Required("taskId 不能为空")
  @IsString("taskId 必须是字符串")
  taskId: string;

  @IsNumber("status 必须是数字")
  status?: number;

  @IsNumber("videoDuration 必须是数字")
  videoDuration?: number;

  @IsNumber("audioDuration 必须是数字")
  audioDuration?: number;

  @IsString("summary 必须是字符串")
  summary?: string;

  @IsString("reqBody 必须是字符串")
  reqBody?: string;

  @IsString("noteContentTitle 必须是字符串")
  noteContentTitle?: string;

  @IsArray("Thinking list must be an array")
  thinkingList?: ThinkingData[];

  @IsString("task_status 必须是字符串")
  taskStatus?: string;

  @IsString("s3CoverImageUrl 必须是字符串")
  s3CoverImageUrl?: string;

  @IsString("s3VideoUrl 必须是字符串")
  s3VideoUrl?: string;

  @IsString("s3AudioUrl 必须是字符串")
  s3AudioUrl?: string;

  @IsString("linkUrl 必须是字符串")
  linkUrl?: string;

  n8nCode?: string;

  @IsString("n8nMessage 必须是字符串")
  n8nMessage?: string;

  @IsString("errMessage 必须是字符串")
  errMessage?: string;

  @IsString("inputAddress 必须是字符串")
  inputAddress?: string;

  @IsArray("Ai make source records must be an array")
  aiMakeSourceRecords?: AiMakeMessageRecordData[];
}

const transformAndValidateMakeGetTaskStatus = (
  response: MakeGetTaskStatusResponse
): MakeGetTaskStatusRawData => {
  const transformed = transformKeys(response);
  try {
    return DataValidator.validateOrThrow(transformed, MakeGetTaskStatusRawData);
  } catch (error: any) {
    console.error("Make get task status data validation failed:", error.message);
    throw error;
  }
};

export interface GenerateMessageResult {
  id: number;
  agentId: string;
  type: string;
  title: string;
  content: string;
  inputText: string;
  thumbnail: string;
  videoUrl: string;
  imageUrls: string[];
  audioUrl: string;
  createdAt: number;
  taskId: string;
  status: number;
  videoDuration: number;
  audioDuration: number;
  summary?: string;
  reqBody?: string;
  noteContentTitle?: string;
  linkUrl?: string;
  coverImageUrl?: string;
  s3CoverImageUrl?: string;
  s3VideoUrl?: string;
  s3AudioUrl?: string;
  s3ImageUrls?: string[];
  thinkingList?: ThinkingData[];
  taskStatus?: string;
  n8nCode?: string;
  n8nMessage?: string;
  errMessage?: string;
  inputAddress?: string;
  aiMakeSourceRecords?: AiMakeMessageRecordData[];
}

const parseDate = (v: any): number => {
  if (v == null) return Date.now();
  if (typeof v === "number") return v * 1000;
  const s = String(v);
  const asInt = parseInt(s, 10);
  if (!isNaN(asInt)) return asInt * 1000;
  const t = Date.parse(s);
  return isNaN(t) ? Date.now() : t;
};

const parseIntSafe = (v: any): number => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = parseInt(String(v), 10);
  return isNaN(n) ? 0 : n;
};

export const getGenerateMessageResult = async ({
  taskId,
}: GetGenerateMessageResultRequest): Promise<GenerateMessageResult> => {
  if (!taskId || !taskId.trim()) throw new Error("taskId 不能为空");

  const res = await api.post<MakeGetTaskStatusResponse>("/make/get_task_status", { task_id: taskId });
  const raw = transformAndValidateMakeGetTaskStatus(res.data);

  const id = raw.recordId ?? null;
  const agentId = String(raw.agentId ?? "");
  const type = String(raw.type ?? "");
  const title = String(raw.title ?? "");
  const content = String(raw.content ?? "");
  const inputText = String(raw.inputText ?? "");
  const thumbnail = String(raw.coverImageUrl ?? "");
  const videoUrl = String(raw.videoUrl ?? "");
  const imageUrlsStr = String(raw.imageUrls ?? "");
  const imageUrls = imageUrlsStr ? imageUrlsStr.split(",").filter(Boolean) : [];
  const audioUrl = String(raw.audioUrl ?? "");
  const createdAt = parseDate(raw.createdAt);
  const outTaskId = String(raw.taskId ?? "");
  const status = parseIntSafe(raw.status);
  const videoDuration = parseIntSafe(raw.videoDuration);
  const audioDuration = parseIntSafe(raw.audioDuration);
  const reqBody = String(raw.reqBody ?? "");
  const noteContentTitle = String(raw.noteContentTitle ?? "");
  const thinkingList = raw.thinkingList ?? [];
  const taskStatus = String(raw.taskStatus ?? "");
  const s3CoverImageUrl = String(raw.s3CoverImageUrl ?? "");
  const s3VideoUrl = String(raw.s3VideoUrl ?? "");
  const s3AudioUrl = String(raw.s3AudioUrl ?? "");
  const coverImageUrl = s3CoverImageUrl || thumbnail;
  const linkUrl = String(raw.linkUrl ?? "");
  const n8nCode = String(raw.n8nCode ?? "");
  const n8nMessage = String(raw.n8nMessage ?? "");
  const summary = String(raw.summary ?? "");
  const errMessage = String(raw.errMessage ?? "");
  const inputAddress = String(raw.inputAddress ?? "");
  const aiMakeSourceRecords = raw.aiMakeSourceRecords ?? [];

  return {
    id,
    agentId,
    type,
    title,
    content,
    inputText,
    thumbnail,
    videoUrl,
    imageUrls,
    audioUrl,
    createdAt,
    taskId: outTaskId,
    status,
    videoDuration,
    audioDuration,
    reqBody,
    noteContentTitle,
    thinkingList,
    taskStatus,
    s3CoverImageUrl,
    s3VideoUrl,
    s3AudioUrl,
    coverImageUrl,
    linkUrl,
    n8nCode,
    n8nMessage,
    summary,
    errMessage,
    inputAddress,
    aiMakeSourceRecords,
  };
};